import { Card } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { MessageSquare, TrendingUp, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { getPlatformStats } from "../lib/api";

interface Stats {
  members: number;
  reviews: number;
  productCoverage: number;
}

export function Community() {
  const [stats, setStats] = useState<Stats>({
    members: 0,
    reviews: 0,
    productCoverage: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const platformStats = await getPlatformStats();
        
        // Calculate product coverage
        const totalGraded = Object.values(platformStats.gradeDistribution).reduce((a, b) => a + b, 0);
        const coverage = platformStats.totalProducts > 0 
          ? Math.round((totalGraded / platformStats.totalProducts) * 100)
          : 0;

        setStats({
          members: platformStats.totalMembers,
          reviews: platformStats.totalReviews,
          productCoverage: coverage,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  const statsDisplay = [
    {
      icon: Users,
      value: loading ? "..." : `${stats.members}${stats.members >= 50 ? "+" : ""}`,
      label: "Members",
    },
    {
      icon: MessageSquare,
      value: loading ? "..." : stats.reviews.toString(),
      label: "Reviews",
    },
    {
      icon: TrendingUp,
      value: loading ? "..." : `${stats.productCoverage}%`,
      label: "Product Coverage",
    },
  ];

  return (
    <section id="community" className="py-24 px-4 sm:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Stats Section */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 mb-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.15
              }
            }
          }}
        >
          {statsDisplay.map((stat, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 text-center hover:shadow-lg transition-all bg-card h-full">
              <div className="w-16 h-16 rounded-2xl gradient-green flex items-center justify-center mx-auto mb-4">
                <stat.icon className="w-8 h-8 text-white" />
              </div>
                <div className="text-4xl mb-2">{stat.value}</div>
                <p className="text-muted-foreground">{stat.label}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Community Showcase */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl mb-6">
              Join a community that
              <span className="block text-primary">cares about your wellness</span>
            </h2>
            


            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="mb-1">Verified Members Only</h4>
                  <p className="text-muted-foreground">Our members can go through verification to ensure a safe and trustworthy community.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="mb-1">Expert Moderation</h4>
                  <p className="text-muted-foreground">Healthcare professionals moderate discussions to ensure accurate, helpful information.</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1569292567777-e5d61a759322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBwZW9wbGUlMjBoYXBweXxlbnwxfHx8fDE3NjAwODQ2MDJ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Community people happy"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}